import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { SERVICE_NAMES } from "@/lib/services";
import { HeaderAccent } from "@/lib/status";
import { UkDateInput } from "@/components/UkDateInput";
import {
  Plus, Phone, Mail, MapPin, X, Trash2, PhoneCall, ClipboardCheck,
  UserCheck, CheckCircle2, HelpCircle, ArrowRight, Pencil,
} from "lucide-react";

const EMPTY = {
  name: "", contact_name: "", contact_phone: "", contact_email: "", address: "", postcode: "",
  date_of_enquiry: "", service_start_date: "",
  care_needs: "", services_interested: [], source: "", notes: "",
};

const STAGES = [
  { key: "new", label: "New enquiry" },
  { key: "telephone", label: "Telephone consultation" },
  { key: "assessment", label: "Assessment visit" },
  { key: "decision", label: "Decision" },
];

function stageOf(e) {
  if (e.status === "converted") return "converted";
  if (e.can_meet_needs) return "decision";
  if (e.assessment_visit_done) return "decision";
  if (e.telephone_consultation_done) return "assessment";
  return "new";
}

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/enquiries").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter the name of the person needing support"); return; }
    try {
      if (editId) {
        const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] ?? EMPTY[k]]));
        await api.put(`/enquiries/${editId}`, body);
        toast.success("Enquiry updated");
      } else {
        await api.post("/enquiries", form);
        toast.success("Enquiry logged");
      }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const edit = (e) => { setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, e[k] ?? EMPTY[k]])) }); setEditId(e.id); setOpen(true); };

  const patch = async (id, body) => {
    try { await api.put(`/enquiries/${id}`, body); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete enquiry for ${e.name}?`)) return;
    try { await api.delete(`/enquiries/${e.id}`); toast.success("Enquiry deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const convert = async (e) => {
    if (!window.confirm(`Register ${e.name} as a client? They'll be added to your client list.`)) return;
    try {
      const r = await api.post(`/enquiries/${e.id}/convert`);
      toast.success(`${e.name} registered as a client`);
      load();
      navigate("/clients");
      return r;
    } catch (err) { toast.error(apiError(err)); }
  };

  const open_ = items.filter((e) => e.status !== "converted");
  const converted = items.filter((e) => e.status === "converted");

  return (
    <div className="space-y-6" data-testid="enquiries-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Enquiries & registration</h1>
          <p className="mt-1 text-ink-soft">From first hello to registered client — track consultations, assessments and decisions.</p>
          <HeaderAccent />
        </div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-enquiry-button"
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
          <Plus size={18} /> New enquiry
        </button>
      </div>

      <div className="space-y-5">
        {open_.length === 0 && <p className="rounded-xl border border-border bg-white p-8 text-center text-ink-muted">No open enquiries. Log a new enquiry to start the registration journey.</p>}
        {open_.map((e) => <EnquiryCard key={e.id} e={e} onPatch={patch} onRemove={remove} onConvert={convert} onEdit={edit} />)}
      </div>

      {converted.length > 0 && (
        <div>
          <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-ink">Registered clients ({converted.length})</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {converted.map((e) => (
              <div key={e.id} className="flex items-center gap-2 rounded-xl border border-sage/40 bg-sage-light/50 p-2 pr-3">
              <button data-testid={`enquiry-converted-${e.id}`} onClick={() => navigate("/clients")}
                className="flex min-w-0 flex-1 items-center gap-3 rounded-lg p-2 text-left transition-colors hover:brightness-95">
                <UserCheck size={20} className="shrink-0 text-sage-dark" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-ink">{e.name}</p>
                  <p className="text-xs text-sage-dark">Registered client</p>
                </div>
                <ArrowRight size={16} className="text-sage-dark" />
              </button>
              <button onClick={() => edit(e)} data-testid={`edit-enquiry-${e.id}`} title="Edit enquiry" className="rounded-lg p-2 text-ink-muted hover:bg-white"><Pencil size={14} /></button>
              <button onClick={() => remove(e)} data-testid={`delete-enquiry-${e.id}`} title="Delete enquiry" className="rounded-lg p-2 text-coral hover:bg-coral-light"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {open && <EnquiryForm form={form} setForm={setForm} editing={!!editId} onClose={() => { setOpen(false); setEditId(null); }} onSave={create} />}
    </div>
  );
}

function EnquiryCard({ e, onPatch, onRemove, onConvert, onEdit }) {
  const stage = stageOf(e);
  const stageIdx = STAGES.findIndex((s) => s.key === stage);
  return (
    <div data-testid={`enquiry-card-${e.id}`} className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-ink">{e.name}</h3>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-soft">
            {e.contact_name && <span className="flex items-center gap-1"><UserCheck size={13} />{e.contact_name}</span>}
            {e.contact_phone && <span className="flex items-center gap-1"><Phone size={13} />{e.contact_phone}</span>}
            {e.contact_email && <span className="flex items-center gap-1"><Mail size={13} />{e.contact_email}</span>}
            {e.address && <span className="flex items-center gap-1"><MapPin size={13} />{e.address}</span>}
          </div>
        </div>
        <div className="flex gap-2">
        <button onClick={() => onEdit(e)} data-testid={`edit-enquiry-${e.id}`} title="Edit enquiry"
          className="rounded-lg border border-border px-2.5 py-2 text-ink transition-colors hover:bg-secondary"><Pencil size={15} /></button>
        <button onClick={() => onRemove(e)} data-testid={`delete-enquiry-${e.id}`} title="Delete enquiry"
          className="rounded-lg border border-border px-2.5 py-2 text-coral transition-colors hover:bg-coral-light"><Trash2 size={15} /></button>
        </div>
      </div>

      {/* Stage progress */}
      <div className="mt-5 flex items-center gap-1">
        {STAGES.map((s, i) => (
          <div key={s.key} className="flex flex-1 items-center gap-1">
            <div className={`flex-1 rounded-full py-1 text-center text-[11px] font-semibold ${i <= stageIdx ? "bg-brand text-white" : "bg-secondary text-ink-muted"}`}>{s.label}</div>
          </div>
        ))}
      </div>

      {e.care_needs && <p className="mt-4 text-sm text-ink-soft"><span className="font-medium text-ink">Care needs: </span>{e.care_needs}</p>}
      {e.services_interested?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {e.services_interested.map((s) => <span key={s} className="rounded-full bg-brand-light px-2.5 py-1 text-xs font-medium text-brand">{s}</span>)}
        </div>
      )}
      {e.notes && <p className="mt-3 whitespace-pre-wrap rounded-lg bg-ivory p-3 text-sm text-ink-soft">{e.notes}</p>}

      {/* Pipeline controls */}
      <div className="mt-5 grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-2">
        <label className="flex items-center gap-2 rounded-lg border border-border bg-ivory/60 px-3 py-2.5 text-sm font-medium text-ink">
          <input type="checkbox" data-testid={`enquiry-phone-${e.id}`} className="h-4 w-4 accent-brand"
            checked={!!e.telephone_consultation_done}
            onChange={(ev) => onPatch(e.id, { telephone_consultation_done: ev.target.checked })} />
          <PhoneCall size={15} className="text-brand" /> Telephone consultation done
        </label>
        <label className="flex items-center gap-2 rounded-lg border border-border bg-ivory/60 px-3 py-2.5 text-sm font-medium text-ink">
          <input type="checkbox" data-testid={`enquiry-assessment-${e.id}`} className="h-4 w-4 accent-brand"
            checked={!!e.assessment_visit_done}
            onChange={(ev) => onPatch(e.id, { assessment_visit_done: ev.target.checked })} />
          <ClipboardCheck size={15} className="text-brand" /> Assessment visit done
        </label>
      </div>

      <div className="mt-4">
        <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-ink"><HelpCircle size={15} className="text-gold-dark" /> Can we meet their needs?</p>
        <div className="flex flex-wrap gap-2">
          {[["yes", "Yes — we can help", "bg-sage text-white"], ["no", "No — cannot meet needs", "bg-coral text-white"], ["", "Undecided", "bg-secondary text-ink"]].map(([val, label, active]) => (
            <button key={val || "none"} data-testid={`enquiry-decision-${val || "none"}-${e.id}`}
              onClick={() => onPatch(e.id, { can_meet_needs: val })}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${e.can_meet_needs === val ? active : "border border-border bg-white text-ink-soft hover:bg-secondary"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {e.can_meet_needs === "yes" && (
        <button onClick={() => onConvert(e)} data-testid={`convert-enquiry-${e.id}`}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sage py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:brightness-105">
          <CheckCircle2 size={17} /> Register as client
        </button>
      )}
      {e.can_meet_needs === "no" && (
        <p className="mt-4 rounded-lg bg-coral-light p-3 text-sm font-medium text-coral-dark">Marked as unable to meet needs. Signpost the family to an alternative service.</p>
      )}
    </div>
  );
}

function EnquiryForm({ form, setForm, editing, onClose, onSave }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={onSave} data-testid="enquiry-form"
        className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl scrollbar-thin">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">{editing ? "Edit enquiry" : "New enquiry"}</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <L label="Person needing support" req><input data-testid="enquiry-name-input" required className={input} value={form.name} onChange={(e) => set("name", e.target.value)} /></L>
          <L label="Enquirer / next of kin"><input className={input} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></L>
          <L label="Contact phone"><input className={input} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></L>
          <L label="Contact email"><input type="email" className={input} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></L>
          <L label="Address" full><input className={input} value={form.address} onChange={(e) => set("address", e.target.value)} /></L>
          <L label="Postcode"><input data-testid="enquiry-postcode-input" className={input} placeholder="e.g. BS1 4ST" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} /></L>
          <L label="Date of enquiry"><UkDateInput testid="enquiry-date-input" value={form.date_of_enquiry} onChange={(v) => set("date_of_enquiry", v)} /></L>
          <L label="Preferred service start date"><UkDateInput testid="enquiry-start-input" value={form.service_start_date} onChange={(v) => set("service_start_date", v)} /></L>
          <L label="How did they hear about us?"><input className={input} placeholder="Website, referral, phone…" value={form.source} onChange={(e) => set("source", e.target.value)} /></L>
          <L label="Care needs" full><textarea rows={2} className={input} value={form.care_needs} onChange={(e) => set("care_needs", e.target.value)} /></L>
          <L label="Services they're interested in" full>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="enquiry-services">
              {SERVICE_NAMES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={(form.services_interested || []).includes(s)} className="accent-brand"
                    onChange={(e) => set("services_interested", e.target.checked ? [...(form.services_interested || []), s] : (form.services_interested || []).filter((x) => x !== s))} />
                  {s}
                </label>
              ))}
            </div>
          </L>
          <L label="Notes" full><textarea rows={3} className={input} placeholder="Anything else worth noting about their situation…" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></L>
        </div>
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button type="submit" data-testid="save-enquiry-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">{editing ? "Save changes" : "Log enquiry"}</button>
        </div>
      </form>
    </div>
  );
}

function L({ label, children, full, req }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium text-ink">{label}{req && <span className="text-coral"> *</span>}</label>
      {children}
    </div>
  );
}
